import { create, type StoreApi, type UseBoundStore } from "zustand";
import { persist } from "zustand/middleware";
import type { Device } from "@/shared/hooks/useDevice";

export type SortType =
  | "default"
  | "created"
  | "old"
  | "replies"
  | "momentum"
  | "soudane";
export type AnimSetting = "always" | "never";

export interface GridColumns {
  mobile: number;
  desktop: number;
}

interface CatalogValues {
  currentSort: SortType;
  /** device 別 gridColumns。
   *  mobile: 既存値 (旧 `columns`) を保持
   *  desktop: PC 用初期値 4 (PR シリーズ期間中は調整余地あり)
   */
  gridColumns: GridColumns;
  showNew: boolean;
  showCount: boolean;
  catalogAnim: AnimSetting;
  /** 新APIではスレッドIDが ULID(string) になったため string[] で保持する */
  lastCatalogIds: string[];
  searchQuery: string;
}

interface CatalogActions {
  setSort: (sort: SortType) => void;
  /** device 別に gridColumns を設定 */
  setGridColumns: (device: Device, columns: number) => void;
  setShowNew: (show: boolean) => void;
  setShowCount: (show: boolean) => void;
  setCatalogAnim: (anim: AnimSetting) => void;
  updateLastCatalogIds: (ids: string[]) => void;
  setSearchQuery: (query: string) => void;
  resetCatalogSettings: () => void;
}

type CatalogState = CatalogValues & CatalogActions;

const DEFAULT_CATALOG_VALUES: CatalogValues = {
  currentSort: "default",
  gridColumns: { mobile: 4, desktop: 4 },
  showNew: true,
  showCount: true,
  catalogAnim: "always",
  lastCatalogIds: [],
  searchQuery: "",
};

interface PersistedV1 {
  currentSort?: SortType;
  columns?: number;
  showNew?: boolean;
  showCount?: boolean;
  catalogAnim?: AnimSetting;
  /** v1 以前は number[] の可能性 (PR-282 以前の旧 API スレッド ID は数値) */
  lastCatalogIds?: (string | number)[];
}

export const useCatalogStore: UseBoundStore<StoreApi<CatalogState>> =
  create<CatalogState>()(
    persist(
      (set) => ({
        ...DEFAULT_CATALOG_VALUES,

        setSort: (sort: SortType) => set({ currentSort: sort }),
        setGridColumns: (device: Device, columns: number) =>
          set((state) => ({
            gridColumns: { ...state.gridColumns, [device]: columns },
          })),
        setShowNew: (show: boolean) => set({ showNew: show }),
        setShowCount: (show: boolean) => set({ showCount: show }),
        setCatalogAnim: (anim: AnimSetting) => set({ catalogAnim: anim }),
        updateLastCatalogIds: (ids: string[]) => set({ lastCatalogIds: ids }),
        setSearchQuery: (query: string) => set({ searchQuery: query }),
        resetCatalogSettings: () => set({ ...DEFAULT_CATALOG_VALUES }),
      }),
      {
        name: "aimg-catalog-settings",
        version: 2,
        partialize: (state: CatalogState) => ({
          currentSort: state.currentSort,
          gridColumns: state.gridColumns,
          showNew: state.showNew,
          showCount: state.showCount,
          catalogAnim: state.catalogAnim,
          lastCatalogIds: state.lastCatalogIds,
        }),
        // version 2: 旧 `columns: number` を `gridColumns: { mobile, desktop }` へ。
        //   mobile は既存値を継承、desktop は default 4 (実験サーバー後で調整)。
        migrate: (persisted: unknown, version: number): CatalogState => {
          if (!persisted || typeof persisted !== "object") {
            return DEFAULT_CATALOG_VALUES as CatalogState;
          }
          if (version < 2) {
            const v1 = persisted as PersistedV1;
            // v1 以前の lastCatalogIds は number[] の可能性があるため String() で正規化。
            // CatalogGrid は ULID(string) で比較するので、未正規化だと「全件 NEW 扱い」になる。
            const normalizedIds = Array.isArray(v1.lastCatalogIds)
              ? v1.lastCatalogIds.map(String)
              : [];
            return {
              ...DEFAULT_CATALOG_VALUES,
              ...v1,
              lastCatalogIds: normalizedIds,
              gridColumns: {
                mobile: typeof v1.columns === "number" ? v1.columns : 4,
                desktop: 4,
              },
            } as CatalogState;
          }
          return persisted as CatalogState;
        },
      },
    ),
  );
